/**
 * Errores de dominio y Problem+json (RFC 9457).
 * Ver §10 del plan. La API NUNCA devuelve texto libre como error: siempre un `code`
 * estable de dominio más un `detail` humano.
 */
import { z } from 'zod';

export const DomainErrorCode = {
  // Autenticación / autorización
  UNAUTHENTICATED: 'UNAUTHENTICATED',
  FORBIDDEN: 'FORBIDDEN',
  /** Credenciales incorrectas (email/contraseña). Genérico para no filtrar si existe. */
  INVALID_CREDENTIALS: 'INVALID_CREDENTIALS',
  OTP_INVALID: 'OTP_INVALID',
  OTP_EXPIRED: 'OTP_EXPIRED',
  OTP_RATE_LIMITED: 'OTP_RATE_LIMITED',
  ACCESS_CODE_INVALID: 'ACCESS_CODE_INVALID',
  ACCESS_CODE_REVOKED: 'ACCESS_CODE_REVOKED',
  /** La cuenta existe pero no tiene rol de administrador. */
  ACCOUNT_NOT_ADMIN: 'ACCOUNT_NOT_ADMIN',
  TOKEN_EXPIRED: 'TOKEN_EXPIRED',
  REFRESH_REUSE_DETECTED: 'REFRESH_REUSE_DETECTED',
  // Validación
  VALIDATION_FAILED: 'VALIDATION_FAILED',
  NOT_FOUND: 'NOT_FOUND',
  CONFLICT: 'CONFLICT',
  // Dominio de apuestas
  FIGHT_NOT_OPEN: 'FIGHT_NOT_OPEN',
  FIGHT_NOT_LOCKED: 'FIGHT_NOT_LOCKED',
  FIGHT_ALREADY_SETTLED: 'FIGHT_ALREADY_SETTLED',
  FIGHT_VERSION_CONFLICT: 'FIGHT_VERSION_CONFLICT',
  FIGHT_SAME_FIGHTER: 'FIGHT_SAME_FIGHTER',
  SIDE_EMPTY_REJECTED: 'SIDE_EMPTY_REJECTED',
  EXPOSURE_LIMIT_EXCEEDED: 'EXPOSURE_LIMIT_EXCEEDED',
  BET_ALREADY_PAID: 'BET_ALREADY_PAID',
  BET_NOT_PAYABLE: 'BET_NOT_PAYABLE',
  BET_NOT_FOUND: 'BET_NOT_FOUND',
  STAKE_BELOW_MIN: 'STAKE_BELOW_MIN',
  STAKE_ABOVE_MAX: 'STAKE_ABOVE_MAX',
  PAYOUT_POOL_INSUFFICIENT: 'PAYOUT_POOL_INSUFFICIENT',
  // Presets
  PRESET_NOT_FOUND: 'PRESET_NOT_FOUND',
  PRESET_NOT_APPROVED: 'PRESET_NOT_APPROVED',
  PRESET_INVALID_TRANSITION: 'PRESET_INVALID_TRANSITION',
  PRESET_PARAMS_INVALID: 'PRESET_PARAMS_INVALID',
  // Idempotencia / infraestructura
  IDEMPOTENCY_KEY_REQUIRED: 'IDEMPOTENCY_KEY_REQUIRED',
  IDEMPOTENCY_KEY_REUSED: 'IDEMPOTENCY_KEY_REUSED',
  RATE_LIMITED: 'RATE_LIMITED',
  INTERNAL: 'INTERNAL',
  SERVICE_UNAVAILABLE: 'SERVICE_UNAVAILABLE',
} as const;

export type DomainErrorCode = (typeof DomainErrorCode)[keyof typeof DomainErrorCode];

export const ProblemSchema = z.object({
  type: z.string().url().or(z.string().startsWith('urn:')),
  title: z.string(),
  status: z.number().int().min(400).max(599),
  detail: z.string(),
  instance: z.string().optional(),
  code: z.string(),
  /** Errores de validación campo a campo (solo para VALIDATION_FAILED). */
  errors: z
    .array(z.object({ path: z.string(), message: z.string() }))
    .optional(),
  /** Datos extra específicos del código (p.ej. reintento permitido en N segundos). */
  meta: z.record(z.unknown()).optional(),
  requestId: z.string().optional(),
});
export type Problem = z.infer<typeof ProblemSchema>;

const STATUS_BY_CODE: Record<DomainErrorCode, number> = {
  UNAUTHENTICATED: 401,
  INVALID_CREDENTIALS: 401,
  ACCESS_CODE_INVALID: 401,
  ACCESS_CODE_REVOKED: 401,
  ACCOUNT_NOT_ADMIN: 403,
  OTP_INVALID: 401,
  OTP_EXPIRED: 401,
  TOKEN_EXPIRED: 401,
  REFRESH_REUSE_DETECTED: 401,
  FORBIDDEN: 403,
  OTP_RATE_LIMITED: 429,
  RATE_LIMITED: 429,
  VALIDATION_FAILED: 422,
  PRESET_PARAMS_INVALID: 422,
  NOT_FOUND: 404,
  BET_NOT_FOUND: 404,
  PRESET_NOT_FOUND: 404,
  CONFLICT: 409,
  FIGHT_VERSION_CONFLICT: 409,
  FIGHT_ALREADY_SETTLED: 409,
  BET_ALREADY_PAID: 409,
  IDEMPOTENCY_KEY_REUSED: 409,
  FIGHT_NOT_OPEN: 409,
  FIGHT_NOT_LOCKED: 409,
  FIGHT_SAME_FIGHTER: 409,
  SIDE_EMPTY_REJECTED: 409,
  BET_NOT_PAYABLE: 409,
  PRESET_INVALID_TRANSITION: 409,
  PRESET_NOT_APPROVED: 409,
  EXPOSURE_LIMIT_EXCEEDED: 409,
  STAKE_BELOW_MIN: 422,
  STAKE_ABOVE_MAX: 422,
  PAYOUT_POOL_INSUFFICIENT: 409,
  IDEMPOTENCY_KEY_REQUIRED: 400,
  INTERNAL: 500,
  SERVICE_UNAVAILABLE: 503,
} as unknown as Record<DomainErrorCode, number>;

export class DomainError extends Error {
  readonly code: DomainErrorCode;
  readonly status: number;
  readonly meta: Record<string, unknown> | undefined;

  constructor(code: DomainErrorCode, detail: string, meta?: Record<string, unknown>) {
    super(detail);
    this.name = 'DomainError';
    this.code = code;
    this.status = STATUS_BY_CODE[code] ?? 500;
    this.meta = meta;
  }

  toProblem(instance?: string, requestId?: string): Problem {
    return {
      type: `https://thepit.local/problems/${this.code.toLowerCase()}`,
      title: TITLES[this.code] ?? 'Error',
      status: this.status,
      detail: this.message,
      code: this.code,
      ...(instance ? { instance } : {}),
      ...(requestId ? { requestId } : {}),
      ...(this.meta ? { meta: this.meta } : {}),
    };
  }
}

const TITLES: Record<DomainErrorCode, string> = {
  UNAUTHENTICATED: 'No autenticado',
  FORBIDDEN: 'Permiso insuficiente',
  INVALID_CREDENTIALS: 'Credenciales inválidas',
  OTP_INVALID: 'Código incorrecto',
  OTP_EXPIRED: 'Código vencido',
  OTP_RATE_LIMITED: 'Demasiados intentos',
  TOKEN_EXPIRED: 'Token vencido',
  REFRESH_REUSE_DETECTED: 'Sesión comprometida',
  VALIDATION_FAILED: 'Datos inválidos',
  NOT_FOUND: 'No encontrado',
  CONFLICT: 'Conflicto de estado',
  FIGHT_NOT_OPEN: 'La pelea no está abierta',
  FIGHT_NOT_LOCKED: 'La pelea no está cerrada',
  FIGHT_ALREADY_SETTLED: 'La pelea ya fue resuelta',
  FIGHT_VERSION_CONFLICT: 'La pelea cambió mientras operabas',
  FIGHT_SAME_FIGHTER: 'Los peleadores deben ser distintos',
  SIDE_EMPTY_REJECTED: 'Lado sin apuestas',
  EXPOSURE_LIMIT_EXCEEDED: 'Límite de exposición excedido',
  BET_ALREADY_PAID: 'El boleto ya fue pagado',
  BET_NOT_PAYABLE: 'El boleto no es pagable',
  BET_NOT_FOUND: 'Boleto no encontrado',
  STAKE_BELOW_MIN: 'Apuesta por debajo del mínimo',
  STAKE_ABOVE_MAX: 'Apuesta por encima del máximo',
  PAYOUT_POOL_INSUFFICIENT: 'Fondos insuficientes para honrar el piso',
  PRESET_NOT_FOUND: 'Preset no encontrado',
  PRESET_NOT_APPROVED: 'Preset no aprobado',
  PRESET_INVALID_TRANSITION: 'Transición de preset inválida',
  PRESET_PARAMS_INVALID: 'Parámetros de preset inválidos',
  IDEMPOTENCY_KEY_REQUIRED: 'Falta la clave de idempotencia',
  IDEMPOTENCY_KEY_REUSED: 'Clave de idempotencia reutilizada con otro contenido',
  RATE_LIMITED: 'Demasiadas solicitudes',
  INTERNAL: 'Error interno',
  SERVICE_UNAVAILABLE: 'Servicio no disponible',
} as unknown as Record<DomainErrorCode, string>;

export function isDomainError(e: unknown): e is DomainError {
  return e instanceof DomainError;
}
